import { useState, useSyncExternalStore, type FormEvent } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import useRecaptcha from "@/hooks/useRecaptcha";
import { RecaptchaProvider } from "@/providers/RecaptchaProvider";
import { OVER_BAR } from "./header/tokens";
import {
  ERROR_TEXT,
  FormField,
  FormFooter,
  LABEL,
  RequiredMark,
  SentPanel,
  fieldErrors,
  useInquiry,
  type ControlProps,
  type Field,
} from "./inquiryForm";
import {
  TRAFFIC,
  submitSalesInquiry,
  type SalesInquiry,
  type TrafficLabel,
} from "./salesInquiry";
import { ACCENT, MID, NOT_WIDE, TINY, marketingPalette } from "./tokens";

/**
 * `#wf-form-Webshare-Sales-Inquiry`, the form behind every "Book a Call" and
 * "Contact Us" on the published site.
 *
 * The published form posted to Webflow, which goes away with the subscription,
 * and Webflow handed the fields to the automation that files the lead. The
 * backend's `salesinquiry` endpoint now stands where Webflow stood: it holds
 * the automation's hook, checks reCAPTCHA and throttles by address, so this
 * dialog collects the fields and asks it to deliver them.
 *
 * When the endpoint does not take it, the form keeps the reader's text and
 * says so under the button, as the published form did, and Send tries again.
 *
 * It is one form per page, whichever control opened it: the published popup is
 * a single element that every "Book a Call" and "Contact Us" points at, and
 * what a reader typed, or the thank-you they reached, is still there when it is
 * opened again. Nothing resets until the page is loaded afresh.
 */

/** The panel beside the form, from the published modal's left column. */
const PITCH = {
  title:
    "Enterprise-Grade Proxy Servers: Scalable Solutions for Premium Performance",
  points: [
    "30M IPs from 195+ global locations",
    "Extremely stable proxies - 99.7% uptime",
    "Custom plan available to fit your needs",
  ],
};

const HEADING = "Contact Us";

type FieldName = "fullName" | "companyName" | "email" | "phone" | "details";

/** The four single-line fields, in the order the published form asks them. */
const TEXT_FIELDS: Field<FieldName>[] = [
  {
    name: "fullName",
    label: "Full Name",
    type: "text",
    autoComplete: "name",
    maxLength: 256,
    minLength: 3,
    invalid: "Please enter your full name",
  },
  {
    name: "companyName",
    label: "Company Name",
    type: "text",
    autoComplete: "organization",
    maxLength: 256,
    minLength: 1,
    invalid: "Please enter your company name",
  },
  {
    name: "email",
    label: "Company Email Address",
    type: "email",
    autoComplete: "email",
    maxLength: 256,
    minLength: 1,
    invalid: "Please enter your company email address",
  },
  {
    name: "phone",
    label: "Phone Number",
    type: "tel",
    autoComplete: "tel",
    maxLength: 256,
    minLength: 1,
    invalid: "Please enter your phone number",
  },
];

const DETAILS: Field<FieldName> = {
  name: "details",
  label: "Please Describe Your Use Case",
  type: "text",
  autoComplete: "off",
  maxLength: 5000,
  minLength: 6,
  invalid: "Please provide more information.",
};

const DETAILS_PLACEHOLDER =
  "Please describe your use case and specify the target website.";

const TRAFFIC_LABEL = "Expected Traffic";

/** The three published radios, by the label each shows. */
const TRAFFIC_OPTIONS = Object.keys(TRAFFIC) as TrafficLabel[];

const TRAFFIC_INVALID = "Please choose your expected traffic";

/** The published `.w-form-done` text, verbatim. */
const SENT_TEXT =
  "Thank you! Our Account Executive will get back to you within 24h";

type Values = Record<FieldName, string> & { traffic: TrafficLabel | "" };
type Errors = Partial<Record<keyof Values, string>>;

const EMPTY: Values = {
  fullName: "",
  companyName: "",
  email: "",
  phone: "",
  details: "",
  traffic: "",
};

function validate(values: Values): Errors {
  const errors: Errors = fieldErrors([...TEXT_FIELDS, DETAILS], values);
  if (!values.traffic) {
    errors.traffic = TRAFFIC_INVALID;
  }
  return errors;
}

/* Published fills with no palette role of their own. */
const PILL_RULE = "rgba(18, 19, 49, 0.25)";
const PILL_CHECKED = "rgba(26, 179, 148, 0.13)";
/** The left column's diamond gradient, inlined from the published SVG. */
const PITCH_GROUND =
  "radial-gradient(ellipse 430px 538px at 116px 0, #002F25 0%, #010504 100%)";
/** `.pop-up_overlay`, a good deal heavier than MUI's own backdrop. */
const BACKDROP = "rgba(0, 0, 0, 0.69)";
/** The card the left column's three claims sit on, over that gradient. */
const PITCH_PANEL = "rgba(255, 255, 255, 0.07)";

const PAPER = {
  flexDirection: "row",
  width: "auto",
  maxWidth: "none",
  borderRadius: "1rem",
  overflow: "hidden",
};

const PITCH_COLUMN = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: "1.5rem",
  width: 1,
  maxWidth: "29.63rem",
  p: "4rem 2rem",
  background: PITCH_GROUND,
  textAlign: "center",
};

const FORM_COLUMN = {
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
  width: "30rem",
  maxWidth: 1,
  p: "4rem 2rem",
  overflowY: "auto",
};

const DIALOG_HEADING = {
  color: marketingPalette.ink,
  fontSize: "2rem",
  fontWeight: 500,
  lineHeight: "2.5rem",
};

const FIELDSET = { m: 0, p: 0, border: 0, mb: "0.75rem", minInlineSize: 0 };

const HEADING_ROW = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const CLOSE_BUTTON = {
  flex: "0 0 auto",
  width: "2.5rem",
  height: "2.5rem",
  borderRadius: "100vw",
  color: marketingPalette.ink,
  transition: "all 0.22s cubic-bezier(0.614, 0.521, 0.041, 1.007)",
  "&:hover": { backgroundColor: marketingPalette.wash },
};

/**
 * Kept in the layout rather than `display: none`, which is what the published
 * radios take: that drops them out of the tab order and out of the accessibility
 * tree, leaving the choice reachable by pointer alone.
 */
const HIDDEN_RADIO = {
  position: "absolute",
  width: "1px",
  height: "1px",
  m: 0,
  p: 0,
  opacity: 0,
};

const PILL = {
  position: "relative",
  m: 0,
  cursor: "pointer",
  "& span": {
    display: "block",
    px: "12px",
    pt: "2px",
    border: `1px solid ${PILL_RULE}`,
    borderRadius: "0.75rem",
    color: marketingPalette.ink,
    fontSize: 14,
    lineHeight: "2em",
    whiteSpace: "nowrap",
    transition: "all 0.1s cubic-bezier(0.77, 0, 0.175, 1)",
  },
  "&:hover span": { borderColor: ACCENT, borderRadius: "1rem" },
  "& input:focus-visible + span": { outline: "revert", outlineOffset: "2px" },
  "& input:checked + span": {
    borderColor: ACCENT,
    backgroundColor: PILL_CHECKED,
    fontWeight: 600,
  },
};

/** The published modal's close glyph, at the size it draws. */
function CloseGlyph() {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      sx={{ width: "1.2rem", height: "1.2rem" }}
    >
      <path
        d="M6 6L18 18M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Box>
  );
}

/** `check-resi`: the modal's own mark, a lighter teal than the site's green tick. */
const PITCH_CHECK =
  "/marketing/webflow/665afd9ceb0adfd84372c069_check-resi.svg";

/** The published modal's left column, which every step under 992 drops. */
function PitchColumn() {
  return (
    <Box sx={[PITCH_COLUMN, { [NOT_WIDE]: { display: "none" } }]}>
      <Typography
        component="p"
        sx={{
          color: "common.white",
          fontSize: "2rem",
          fontWeight: 500,
          lineHeight: "2.5rem",
        }}
      >
        {PITCH.title}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          p: "2rem",
          borderRadius: "0.75rem",
          backgroundColor: PITCH_PANEL,
        }}
      >
        {PITCH.points.map((point) => (
          <Box
            key={point}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              color: "common.white",
              textAlign: "left",
            }}
          >
            <Box
              component="img"
              src={PITCH_CHECK}
              alt=""
              width={20}
              height={20}
              sx={{ display: "block", flex: "0 0 auto" }}
            />
            <Box>{point}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TrafficChoice({
  name,
  value,
  error,
  fieldId,
  errorId,
  onChange,
}: Omit<ControlProps, "onChange"> & {
  name: string;
  onChange: (value: TrafficLabel) => void;
}) {
  return (
    <Box component="fieldset" sx={FIELDSET}>
      <Box component="legend" sx={[LABEL, { p: 0 }]}>
        {TRAFFIC_LABEL}
        <RequiredMark />
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "7px", pt: "5px" }}>
        {TRAFFIC_OPTIONS.map((option, index) => (
          <Box component="label" key={option} sx={PILL}>
            <Box
              component="input"
              type="radio"
              name={name}
              // Only the first carries the group's id: it is where a failed
              // submit sends focus, and a radio group is entered at its head.
              id={index === 0 ? fieldId : undefined}
              value={option}
              checked={value === option}
              onChange={() => onChange(option)}
              required
              aria-describedby={error ? errorId : undefined}
              sx={HIDDEN_RADIO}
            />
            <span>{option}</span>
          </Box>
        ))}
      </Box>
      {error ? (
        <Typography id={errorId} role="alert" sx={ERROR_TEXT}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}

function InquiryDialog({ open, onClose }: InquiryDialogProps) {
  const { onSubmitWithReCAPTCHA } = useRecaptcha();
  const {
    scope,
    values,
    errors,
    status,
    setStatus,
    edit,
    firstProblem,
    fieldId,
    errorId,
  } = useInquiry(EMPTY);

  const headingId = `${scope}heading`;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const firstInvalid = firstProblem(validate(values));
    const { traffic } = values;
    if (firstInvalid || traffic === "") {
      document.getElementById(fieldId(firstInvalid ?? "traffic"))?.focus();
      return;
    }

    setStatus("sending");
    const inquiry: SalesInquiry = { ...values, traffic };
    // No token means the captcha could not run, which the endpoint would refuse
    // anyway; the reader sees the same line either way and can try again.
    const token = await onSubmitWithReCAPTCHA();
    const sent = token ? await submitSalesInquiry(inquiry, token) : false;
    setStatus(sent ? "sent" : "failed");
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby={headingId}
      // MUI's modal layer sits at 1300, well under the fixed bar, which would
      // leave the header undimmed and the open drawer over the dialog. The
      // published overlay takes 9999, which is `OVER_BAR` exactly.
      sx={{ zIndex: OVER_BAR }}
      slotProps={{
        paper: { sx: PAPER },
        backdrop: { sx: { backgroundColor: BACKDROP } },
      }}
    >
      <PitchColumn />

      <Box
        sx={[
          FORM_COLUMN,
          { [NOT_WIDE]: { p: "2rem" } },
          { [MID]: { width: 1 } },
          { [TINY]: { gap: "0.8rem", p: "1rem" } },
        ]}
      >
        <Box sx={HEADING_ROW}>
          <Typography
            id={headingId}
            component="h2"
            sx={[
              DIALOG_HEADING,
              { [NOT_WIDE]: { fontSize: "1.7rem" } },
              { [TINY]: { fontSize: "1.5rem", lineHeight: "2rem" } },
            ]}
          >
            {HEADING}
          </Typography>
          <ButtonBase
            type="button"
            onClick={onClose}
            aria-label="Close"
            sx={CLOSE_BUTTON}
          >
            <CloseGlyph />
          </ButtonBase>
        </Box>

        {status === "sent" ? (
          <SentPanel text={SENT_TEXT} />
        ) : (
          <Box component="form" onSubmit={submit} noValidate>
            {[...TEXT_FIELDS, DETAILS].map((spec) => (
              <FormField
                key={spec.name}
                spec={spec}
                multiline={spec === DETAILS}
                placeholder={spec === DETAILS ? DETAILS_PLACEHOLDER : undefined}
                value={values[spec.name]}
                error={errors[spec.name]}
                fieldId={fieldId(spec.name)}
                errorId={errorId(spec.name)}
                onChange={(next) => edit(spec.name, next)}
              />
            ))}
            <TrafficChoice
              name={`${scope}traffic`}
              value={values.traffic}
              error={errors.traffic}
              fieldId={fieldId("traffic")}
              errorId={errorId("traffic")}
              onChange={(next) => edit("traffic", next)}
            />

            <FormFooter status={status} />
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

type InquiryDialogProps = { open: boolean; onClose: () => void };

/*
  Whether the page's one dialog is open. A module store rather than context so
  a trigger anywhere on a page can open the dialog the header renders, without
  a provider threaded through every page tree that renders the header.
*/
let isOpen = false;
const listeners = new Set<() => void>();

function setOpen(next: boolean) {
  if (isOpen === next) {
    return;
  }
  isOpen = next;
  for (const listener of listeners) {
    listener();
  }
}

/** What every "Book a Call" and "Contact Us" control calls. */
export function openSalesInquiry() {
  setOpen(true);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Closed in the prerender, and on every page until a reader asks. */
function useSalesInquiryOpen() {
  return useSyncExternalStore(
    subscribe,
    () => isOpen,
    () => false
  );
}

/**
 * Rendered once per page, by the header. The marketing routes render under
 * `MarketingShell`, which carries the theme and nothing else, so the reCAPTCHA
 * provider the app shell mounts is not there. It lives here instead, and only
 * from the first open: mounting it loads Google's script, which has no
 * business on a page whose reader never asks for the form. Flipped during
 * render rather than in an effect so the first open already renders inside the
 * provider and nothing remounts.
 */
export default function SalesInquiryDialog() {
  const open = useSalesInquiryOpen();
  const [asked, setAsked] = useState(false);
  if (open && !asked) {
    setAsked(true);
  }
  const dialog = <InquiryDialog open={open} onClose={() => setOpen(false)} />;
  return asked ? <RecaptchaProvider>{dialog}</RecaptchaProvider> : dialog;
}
